import { ClientTypeDto, CreateClientTypeDto, UpdateClientTypeDto } from "@dialogics/shared/dto/types"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { ModalProps } from "../../component/modal"
import { useCreateCallTypeMutation, useUpdateCallTypeMutation } from "./service"

type CallTypeFormProps = {
  callType?: ClientTypeDto
  organizationGroupId: number
} & ModalProps

export const CallTypeForm = ({ isOpen, onClose, callType, organizationGroupId }: CallTypeFormProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateClientTypeDto | UpdateClientTypeDto>({
    defaultValues: {},
  })
  const [createCallType, { isLoading: isCreating }] = useCreateCallTypeMutation()
  const [updateCallType, { isLoading: isUpdating }] = useUpdateCallTypeMutation()

  useEffect(() => {
    if (callType) {
      reset({
        ...callType,
        facts_description: callType.facts_description ? JSON.stringify(callType.facts_description, null, 2) : "{}",
        score_rules: callType.score_rules ? JSON.stringify(callType.score_rules, null, 2) : "{}",
      })
    } else {
      reset({
        code: "",
        title: "",
        description: "",
        is_commercial: false,
        organization_group_id: organizationGroupId,
        facts_description: "{}",
        score_rules: "{}",
      })
    }
  }, [callType, reset, organizationGroupId])

  const validateJson = (value: string) => {
    if (value.trim() === "") return true
    try {
      JSON.parse(value)
      return true
    } catch {
      return "Неверный JSON"
    }
  }

  const onSubmit = async (data: CreateClientTypeDto | UpdateClientTypeDto) => {
    const parsedData = {
      ...data,
      facts_description: data.facts_description === "" ? null : JSON.parse(data.facts_description as string),
      score_rules: data.score_rules === "" ? null : JSON.parse(data.score_rules as string),
    }
    if (callType) {
      await updateCallType({ id: callType.id, data: parsedData })
    } else {
      await createCallType(parsedData as CreateClientTypeDto)
    }
    onClose()
  }

  return (
    <div className="max-w-7xl mx-auto bg-white dark:bg-base-200 p-6 sm:p-8 rounded-lg shadow-sm">
      <h3 className="font-bold text-lg">{callType ? "Редактировать тип звонка" : "Создать тип звонка"}</h3>
      <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-2 gap-4">
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Код</legend>
          <input
            {...register("code", {
              required: "Код обязателен",
              pattern: { value: /^[a-z]+$/, message: "Только строчные латинские символы" },
            })}
            className={`input input-bordered w-full ${errors.code ? "input-error" : ""}`}
          />
          {errors.code && <span className="text-error text-xs">{errors.code.message}</span>}
        </fieldset>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Название</legend>
          <input
            {...register("title", { required: "Название обязательно" })}
            className={`input input-bordered w-full ${errors.title ? "input-error" : ""}`}
          />
          {errors.title && <span className="text-error text-xs">{errors.title.message}</span>}
        </fieldset>
        <fieldset className="fieldset col-span-2">
          <legend className="fieldset-legend">Описание</legend>
          <textarea {...register("description")} className="textarea textarea-bordered w-full" />
        </fieldset>

        <fieldset className="fieldset">
          <legend className="fieldset-legend">Описание фактов</legend>
          <textarea
            {...register("facts_description", { validate: validateJson })}
            className="textarea textarea-bordered w-full h-100"
          />
          {errors.facts_description && <span className="text-error text-xs">{errors.facts_description.message}</span>}
        </fieldset>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Правила оценки</legend>
          <textarea
            {...register("score_rules", { validate: validateJson })}
            className="textarea textarea-bordered w-full h-100"
          />
          {errors.score_rules && <span className="text-error text-xs">{errors.score_rules.message}</span>}
        </fieldset>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Коммерческий</legend>
          <input type="checkbox" {...register("is_commercial")} className="checkbox" />
        </fieldset>
        <div className="modal-action">
          <button type="button" className="btn" onClick={onClose} disabled={isCreating || isUpdating}>
            Отмена
          </button>
          <button type="submit" className="btn btn-primary" disabled={isCreating || isUpdating}>
            {isCreating || isUpdating ? "Сохранение..." : "Сохранить"}
          </button>
        </div>
      </form>
    </div>
  )
}
